import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
    loadStripe,
    type Stripe,
    type StripeElements,
    type StripeElementsOptions,
} from '@stripe/stripe-js';
import {
    Elements,
    PaymentElement,
    useStripe,
    useElements,
} from '@stripe/react-stripe-js';
import { Button } from '@rocco/components/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@rocco/components/dialog';
import { LoaderCircle, X } from 'lucide-react';
import { useFetcher } from '@remix-run/react';
import type { PaymentIntentCreateAction } from '~/routes/remix-api.payment-intent.create';
import {
    assertSuccessResponse,
    assertErrorResponse,
} from '@modules/root/graphql/responses';
import { usePaymentPopup } from '../../hooks/usePaymentPopup';
import { useConfigContext } from '@archipro-website/config/bindings/react';

// Type definition for Stripe error
interface StripeError {
    message?: string;
    type?: string;
    code?: string;
}

// Type definition for confirmPayment method
interface StripeWithConfirmPayment extends Stripe {
    confirmPayment(options: {
        elements: StripeElements;
        confirmParams: {
            return_url: string;
        };
        redirect: 'if_required';
    }): Promise<{ error?: StripeError }>;
}

// Type definition for Elements options
interface ElementsOptions extends StripeElementsOptions {
    clientSecret: string;
    appearance: {
        theme: 'stripe';
        variables: {
            colorPrimary: string;
            colorBackground: string;
            colorText: string;
            colorDanger: string;
            fontFamily: string;
            spacingUnit: string;
            borderRadius: string;
        };
    };
}

// Payment intent data will be fetched from the backend API via GraphQL

/**
 * Payment form component that handles Stripe payment element
 */
const PaymentForm: React.FC<{
    onPaymentSuccess?: () => void;
    onPaymentError?: (error: string) => void;
    invoicePaymentId?: string | null;
}> = ({ onPaymentSuccess, onPaymentError, invoicePaymentId }) => {
    const { closePopup } = usePaymentPopup();
    const stripe = useStripe();
    const elements = useElements();
    const [isProcessing, setIsProcessing] = useState(false);
    const [message, setMessage] = useState<string | null>(null);
    const [isPaymentElementReady, setIsPaymentElementReady] = useState(false);

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();

        if (!stripe || !elements || !isPaymentElementReady) {
            return;
        }

        setIsProcessing(true);
        setMessage(null);

        try {
            // Confirm the payment using the payment element
            const { error } = await (
                stripe as StripeWithConfirmPayment
            ).confirmPayment({
                elements,
                confirmParams: {
                    return_url: window.location.href,
                },
                redirect: 'if_required',
            });

            if (error) {
                // Only display server-side errors (card_error, api_error, etc.)
                // Stripe's PaymentElement handles inline validation errors (validation_error)
                // Error types: https://docs.stripe.com/error-handling
                const shouldDisplayError = error.type !== 'validation_error';

                if (shouldDisplayError) {
                    const errorMessage = error.message || 'Payment failed';
                    setMessage(errorMessage);
                    onPaymentError?.(errorMessage);
                } else {
                    // Clear any previous error messages for validation errors
                    setMessage('');
                    // Still call the callback for consistency
                    onPaymentError?.(error.message || 'Validation error');
                }
            } else {
                // Set invoice payment as processing after successful payment (fire and forget, no error display)
                if (invoicePaymentId) {
                    const formData = new FormData();
                    formData.append('invoicePaymentId', invoicePaymentId);

                    fetch('/remix-api/invoice-payment-set-processing-payment', {
                        method: 'POST',
                        body: formData,
                    }).catch(() => {
                        // Silently ignore errors - payment processing will continue on the backend
                    });
                }

                setMessage('Payment successful!');
                onPaymentSuccess?.();
            }
        } catch (err) {
            const errorMessage =
                err instanceof Error
                    ? err.message
                    : 'An unexpected error occurred';
            setMessage(errorMessage);
            onPaymentError?.(errorMessage);
        } finally {
            setIsProcessing(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-6" id="payment-form">
            <PaymentElement
                onReady={() => setIsPaymentElementReady(true)}
                options={{
                    layout: 'tabs',
                }}
            />
            {message && (
                <div
                    className={`rounded-md p-3 text-sm ${
                        message.includes('successful')
                            ? 'bg-green-50 text-green-700 border-green-200 border'
                            : 'border border-red-200 bg-red-50 text-red-700'
                    }`}
                >
                    {message}
                </div>
            )}
            <div className="flex flex-row gap-3 pb-8 md:pb-0">
                <Button
                    type="submit"
                    disabled={
                        !stripe ||
                        !elements ||
                        !isPaymentElementReady ||
                        isProcessing
                    }
                    className="flex-1"
                >
                    {isProcessing ? (
                        <>
                            <LoaderCircle className="mr-2 h-4 w-4 animate-spin" />
                            Processing...
                        </>
                    ) : (
                        'Pay Now'
                    )}
                </Button>
                <Button
                    type="button"
                    color="white"
                    onClick={closePopup}
                    disabled={isProcessing}
                >
                    Cancel
                </Button>
            </div>
        </form>
    );
};

/**
 * Stripe payment popup component with payment element
 */
export const StripePaymentPopup: React.FC<{
    onPaymentSuccess?: () => void;
    onPaymentError?: (error: string) => void;
}> = ({ onPaymentSuccess, onPaymentError }) => {
    const {
        isPaymentPopupOpen,
        closePopup,
        currentUrlSegment,
        stripeAccount,
        currentPaymentId,
        publishableKey,
    } = usePaymentPopup();

    const [clientSecret, setClientSecret] = useState<string | null>(null);
    const [paymentIntentData, setPaymentIntentData] = useState<{
        amount: number;
        currency: string;
    } | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    /** Error message to display when payment intent creation fails */
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const hasCreatedPaymentIntent = useRef(false);
    const config = useConfigContext();

    // Create Stripe promise using publishable key from invoice or fallback to config
    const stripePromise = useMemo(() => {
        // Use publishable key from invoice's Stripe config, fallback to global config
        const key = publishableKey || config.stripeEcommerce.publishableKey;

        // Create Stripe instance with stripeAccount if available
        // We need to include stripeAccount for Stripe Connect functionality
        return loadStripe(key, {
            stripeAccount: stripeAccount || undefined,
        });
    }, [stripeAccount, publishableKey, config.stripeEcommerce.publishableKey]);

    const paymentIntentFetcher = useFetcher<PaymentIntentCreateAction>();

    useEffect(() => {
        if (
            isPaymentPopupOpen &&
            currentUrlSegment &&
            currentPaymentId &&
            !hasCreatedPaymentIntent.current &&
            paymentIntentFetcher.state === 'idle'
        ) {
            // Create payment intent when popup opens (only once and when fetcher is idle)
            setIsLoading(true);
            hasCreatedPaymentIntent.current = true;
            paymentIntentFetcher.submit(
                {
                    invoiceUuid: currentUrlSegment,
                    invoicePaymentId: currentPaymentId,
                },
                {
                    method: 'post',
                    action: '/remix-api/payment-intent/create',
                }
            );
        } else if (!isPaymentPopupOpen) {
            // Reset when popup closes
            setClientSecret(null);
            setPaymentIntentData(null);
            setIsLoading(false);
            setErrorMessage(null);
            hasCreatedPaymentIntent.current = false;
        }
    }, [
        isPaymentPopupOpen,
        currentUrlSegment,
        currentPaymentId,
        paymentIntentFetcher,
    ]);

    // Handle payment intent creation response
    useEffect(() => {
        if (
            paymentIntentFetcher.state === 'idle' &&
            paymentIntentFetcher.data
        ) {
            const isSuccess = assertSuccessResponse(paymentIntentFetcher.data);
            const isError = assertErrorResponse(paymentIntentFetcher.data);

            if (isSuccess) {
                // Success case
                if (
                    'data' in paymentIntentFetcher.data &&
                    paymentIntentFetcher.data.data
                ) {
                    const {
                        clientSecret: newClientSecret,
                        amount: intentAmount,
                        currency: intentCurrency,
                    } = paymentIntentFetcher.data.data;
                    setClientSecret(newClientSecret);
                    setPaymentIntentData({
                        amount: intentAmount,
                        currency: intentCurrency,
                    });
                    setErrorMessage(null);
                }
            } else if (isError) {
                // Error case
                if ('message' in paymentIntentFetcher.data) {
                    const error = String(paymentIntentFetcher.data.message);
                    setErrorMessage(error);
                    onPaymentError?.(error);
                }
            }
            setIsLoading(false);
        }
    }, [paymentIntentFetcher.state, paymentIntentFetcher.data, onPaymentError]);

    // Handle browser back button to close popup on mobile
    useEffect(() => {
        if (isPaymentPopupOpen) {
            // Push a dummy state to the history when dialog opens
            window.history.pushState({ paymentPopup: true }, '');

            // Listen for back button press
            const handlePopState = (_event: PopStateEvent) => {
                // Close the popup when back button is pressed
                closePopup();
            };

            window.addEventListener('popstate', handlePopState);

            // Cleanup function - only remove the event listener
            return () => {
                window.removeEventListener('popstate', handlePopState);
            };
        } else if (window.history.state?.paymentPopup) {
            // Remove the dummy state only when the popup is closed
            window.history.back();
        }
    }, [isPaymentPopupOpen, closePopup]);

    const options: ElementsOptions | undefined = clientSecret
        ? {
              clientSecret,
              appearance: {
                  theme: 'stripe' as const,
                  variables: {
                      colorPrimary: '#0570de',
                      colorBackground: '#ffffff',
                      colorText: '#30313d',
                      colorDanger: '#df1b41',
                      fontFamily: 'Ideal Sans, system-ui, sans-serif',
                      spacingUnit: '2px',
                      borderRadius: '4px',
                  },
              },
          }
        : undefined;

    return (
        <Dialog open={isPaymentPopupOpen}>
            <DialogContent
                closeButton={false}
                className="max-h-[100vh] overflow-y-auto"
            >
                <DialogHeader>
                    <DialogTitle className="flex items-center justify-between">
                        <span>Complete Payment</span>
                        <Button
                            color="white"
                            size="compact"
                            onClick={closePopup}
                            className="h-6 w-6 p-0"
                        >
                            <X className="h-4 w-4" />
                        </Button>
                    </DialogTitle>
                    <DialogDescription>
                        Enter your payment details to securely complete your
                        purchase.
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                    {paymentIntentData && (
                        <div className="text-center">
                            <p className="text-sm text-gray-600">
                                Amount to pay
                            </p>
                            <p className="text-2xl font-semibold">
                                {(paymentIntentData?.currency?.toUpperCase() ||
                                    'USD') +
                                    ' ' +
                                    (paymentIntentData.amount / 100).toFixed(2)}
                            </p>
                            {/* Note: Stripe amounts are in cents, so we divide by 100 to display the correct amount */}
                        </div>
                    )}

                    {isLoading ||
                    paymentIntentFetcher.state === 'submitting' ? (
                        <div className="flex items-center justify-center py-8">
                            <LoaderCircle className="h-6 w-6 animate-spin" />
                            <span className="ml-2 text-sm text-gray-600">
                                Processing payment details...
                            </span>
                        </div>
                    ) : errorMessage ? (
                        <div className="py-4">
                            <div className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-700">
                                {errorMessage}
                            </div>
                            <div className="mt-4 flex justify-end">
                                <Button color="white" onClick={closePopup}>
                                    Close
                                </Button>
                            </div>
                        </div>
                    ) : (
                        !(options && stripeAccount) && (
                            <div className="flex items-center justify-center py-8">
                                <LoaderCircle className="h-6 w-6 animate-spin" />
                                <span className="ml-2 text-sm text-gray-600">
                                    Initializing payment...
                                </span>
                            </div>
                        )
                    )}
                    {options && stripeAccount && (
                        <Elements stripe={stripePromise} options={options}>
                            <PaymentForm
                                invoicePaymentId={
                                    currentPaymentId
                                        ? String(currentPaymentId)
                                        : null
                                }
                                onPaymentSuccess={() => {
                                    closePopup();
                                    onPaymentSuccess?.();
                                }}
                                onPaymentError={(error) => {
                                    onPaymentError?.(error);
                                }}
                            />
                        </Elements>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
};
